import * as React from 'react'
import { EmojiPicker as FrimousseEmojiPicker } from 'frimousse'
import type { EmojiPickerListCategoryHeaderProps } from 'frimousse'

import { Popover } from '../Popover'
import type { PopoverContentProps, PopoverProps } from '../Popover'
import { VisuallyHidden } from '../VisuallyHidden'
import {
  CarIcon,
  ClapperboardIcon,
  XIcon,
  FlagIcon,
  FruitIcon,
  PawPrintIcon,
  SearchIcon,
  ShapesIcon,
  SmileIcon,
  UserIcon,
  VolleyballIcon,
} from '../icons'

import styles from './EmojiPicker.module.css'

import type { JSX } from 'react'

// Keep the nav in sync just before the next sticky category header reaches the top.
const ACTIVE_CATEGORY_SCROLL_THRESHOLD = 8
const DEFAULT_COLUMNS = 9
const EMPTY_FREQUENTLY_USED_EMOJIS: string[] = []

const CATEGORY_LABELS: Record<string, string> = {
  'Smileys & emotion': 'Smileys & People',
}

const EMOJI_CATEGORIES = [
  { label: 'Smileys & emotion', icon: SmileIcon },
  { label: 'People & body', icon: UserIcon },
  { label: 'Animals & nature', icon: PawPrintIcon },
  { label: 'Food & drink', icon: FruitIcon },
  { label: 'Activities', icon: VolleyballIcon },
  { label: 'Travel & places', icon: CarIcon },
  { label: 'Objects', icon: ClapperboardIcon },
  { label: 'Symbols', icon: ShapesIcon },
  { label: 'Flags', icon: FlagIcon },
] as const

/**
 * Props for the EmojiPicker component.
 */
type EmojiPickerProps = Pick<
  PopoverProps,
  'open' | 'defaultOpen' | 'onOpenChange' | 'modal'
> &
  Pick<
    PopoverContentProps,
    | 'side'
    | 'align'
    | 'sideOffset'
    | 'alignOffset'
    | 'onEscapeKeyDown'
    | 'onOpenAutoFocus'
  > & {
    /** Trigger element that opens the picker. */
    children: React.ReactNode
    /** Called with the selected emoji character. */
    onEmojiSelect: (emoji: string) => void
    /**
     * Number of emoji columns in the grid.
     * @default 9
     */
    columns?: number
    /** Emoji characters to show in the "Frequently used" section. */
    frequentlyUsedEmojis?: string[]
    /**
     * Placeholder text for the emoji search input.
     * @default 'Search'
     */
    searchPlaceholder?: string
    /**
     * Message shown when search returns no results.
     * @default 'No emoji found.'
     */
    emptyMessage?: string
  }

const CategoryHeader = ({
  category,
  ...props
}: EmojiPickerListCategoryHeaderProps): JSX.Element => {
  return (
    <div {...props} data-category-label={category.label}>
      {CATEGORY_LABELS[category.label] ?? category.label}
    </div>
  )
}

type CategoryNavProps = {
  activeCategory: string
  setActiveCategory: (category: string) => void
  viewportRef: React.RefObject<HTMLDivElement>
}

const CategoryNav = ({
  activeCategory,
  setActiveCategory,
  viewportRef,
}: CategoryNavProps): JSX.Element => {
  const scrollToCategory = React.useCallback(
    (categoryLabel: string) => {
      const viewport = viewportRef.current
      if (!viewport) return

      setActiveCategory(categoryLabel)

      const categoryHeaders = Array.from(
        viewport.querySelectorAll('[frimousse-category-header]'),
      )

      const targetHeader = categoryHeaders.find(
        (header) =>
          header.getAttribute('data-category-label') === categoryLabel,
      )
      const targetSection = targetHeader?.parentElement
      if (targetSection instanceof HTMLElement) {
        if (typeof viewport.scrollTo === 'function') {
          viewport.scrollTo({ top: targetSection.offsetTop })
          return
        }

        viewport.scrollTop = targetSection.offsetTop
      }
    },
    [setActiveCategory, viewportRef],
  )

  return (
    <nav className={styles.categoryNav} aria-label="Emoji categories">
      {EMOJI_CATEGORIES.map((category) => {
        const Icon = category.icon
        return (
          <button
            key={category.label}
            type="button"
            className={styles.categoryButton}
            data-active={activeCategory === category.label ? true : undefined}
            aria-current={
              activeCategory === category.label ? 'location' : undefined
            }
            onClick={() => scrollToCategory(category.label)}
            aria-label={`Jump to ${category.label}`}
          >
            <Icon size="medium" />
          </button>
        )
      })}
    </nav>
  )
}

const EmojiPicker = React.forwardRef<HTMLDivElement, EmojiPickerProps>(
  (
    {
      children,
      onEmojiSelect,
      columns = DEFAULT_COLUMNS,
      frequentlyUsedEmojis = EMPTY_FREQUENTLY_USED_EMOJIS,
      searchPlaceholder = 'Search',
      emptyMessage = 'No emoji found.',
      open,
      defaultOpen,
      onOpenChange,
      modal,
      side = 'top',
      align = 'center',
      sideOffset,
      alignOffset,
      onEscapeKeyDown,
      onOpenAutoFocus,
    },
    ref,
  ): JSX.Element => {
    const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false)
    const [searchQuery, setSearchQuery] = React.useState('')
    const [activeCategory, setActiveCategory] = React.useState<string>(
      EMOJI_CATEGORIES[0].label,
    )
    const searchId = React.useId()
    const instructionsId = React.useId()
    const searchRef = React.useRef<HTMLInputElement | null>(null)
    const viewportRef = React.useRef<HTMLDivElement | null>(null)
    const isOpen = open ?? internalOpen
    const frequentlyUsed = React.useMemo(
      () => frequentlyUsedEmojis.map((emoji) => ({ emoji, label: emoji })),
      [frequentlyUsedEmojis],
    )

    const handleOpenChange = React.useCallback(
      (nextOpen: boolean): void => {
        if (open === undefined) {
          setInternalOpen(nextOpen)
        }
        if (!nextOpen) setSearchQuery('')
        onOpenChange?.(nextOpen)
      },
      [onOpenChange, open],
    )

    const handleEmojiSelect = React.useCallback(
      (emoji: string): void => {
        onEmojiSelect(emoji)
        handleOpenChange(false)
      },
      [handleOpenChange, onEmojiSelect],
    )

    const handleSearchClear = React.useCallback((): void => {
      setSearchQuery('')
      requestAnimationFrame(() => searchRef.current?.focus())
    }, [])

    const handleViewportScroll = React.useCallback(
      (event: React.UIEvent<HTMLDivElement>): void => {
        const viewport = event.currentTarget
        const headers = Array.from(
          viewport.querySelectorAll<HTMLElement>('[frimousse-category-header]'),
        )
        const visibleHeader = headers.reduce<HTMLElement | null>(
          (currentHeader, header) => {
            const section = header.parentElement
            if (
              section instanceof HTMLElement &&
              section.offsetTop <=
                viewport.scrollTop + ACTIVE_CATEGORY_SCROLL_THRESHOLD
            ) {
              return header
            }
            return currentHeader
          },
          null,
        )
        const category = visibleHeader?.getAttribute('data-category-label')
        if (!category) return

        const matchingCategory = EMOJI_CATEGORIES.find(
          ({ label }) => category === label,
        )

        if (matchingCategory) setActiveCategory(matchingCategory.label)
      },
      [],
    )

    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange} modal={modal}>
        <Popover.Trigger className={styles.trigger}>{children}</Popover.Trigger>
        <Popover.Content
          ref={ref}
          align={align}
          side={side}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          onEscapeKeyDown={onEscapeKeyDown}
          aria-label="Emoji picker"
          onOpenAutoFocus={(event) => {
            onOpenAutoFocus?.(event)
            if (event.defaultPrevented) return

            event.preventDefault()
            searchRef.current?.focus()
          }}
          className={styles.content}
        >
          <FrimousseEmojiPicker.Root
            columns={columns}
            frequently={frequentlyUsed}
            frequentlyLabel="Frequently used"
            onEmojiSelect={({ emoji }) => {
              if (!emoji) return

              handleEmojiSelect(emoji)
            }}
          >
            <VisuallyHidden id={instructionsId}>
              Use the arrow keys to browse emoji results, then press Enter to
              select an emoji.
            </VisuallyHidden>
            <CategoryNav
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              viewportRef={viewportRef}
            />
            <div className={styles.searchWrapper}>
              <SearchIcon size="medium" className={styles.searchIcon} />
              <FrimousseEmojiPicker.Search
                ref={searchRef}
                id={searchId}
                name="emoji-search"
                aria-label="Search emoji"
                aria-describedby={instructionsId}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={searchPlaceholder}
              />
              {searchQuery !== '' && (
                <button
                  type="button"
                  className={styles.clearSearchButton}
                  onClick={handleSearchClear}
                  aria-label="Clear emoji search"
                >
                  <XIcon size="medium" />
                </button>
              )}
            </div>
            <FrimousseEmojiPicker.Viewport
              ref={viewportRef}
              onScroll={handleViewportScroll}
            >
              <FrimousseEmojiPicker.Loading />
              <FrimousseEmojiPicker.Empty className={styles.empty}>
                {emptyMessage}
              </FrimousseEmojiPicker.Empty>

              <FrimousseEmojiPicker.List
                aria-describedby={instructionsId}
                aria-label="Emoji results"
                components={{ CategoryHeader }}
              />
            </FrimousseEmojiPicker.Viewport>
          </FrimousseEmojiPicker.Root>
        </Popover.Content>
      </Popover>
    )
  },
)

EmojiPicker.displayName = 'EmojiPicker'

export { EmojiPicker }
export type { EmojiPickerProps }
